import React from 'react';
import { Helmet } from '@plone/volto/helpers';

import useSiteConfig from '../../helpers/useSiteConfig';
import {
  SITE_CHOICES,
  SITE_BASE_URLS,
} from '../../config/searchRegistry.generated';
import { PUBLISHER_NAME, PUBLISHER_LEGAL_NAME } from '../../siteConfig/branding';

/**
 * SEO head for the SITE ROOT — the one page that was still trusting core to
 * work out its own address. (item 12)
 *
 * Every other public surface already asserts its own absolute URLs from
 * SiteConfig.baseUrl: DocumentSEO for content, BrowseSEO for the category and
 * paged listings, the /search shadow for the search tool. The root was the
 * gap, and it fell through to core's ContentMetadataTags, which builds the
 * canonical with `toPublicURL` -> `config.settings.publicURL` -> whatever
 * seamless mode last derived from the request. Audited through the internal
 * address, the home page's canonical was literally
 * `http://127.0.0.1:9090` — one node process announcing itself, to crawlers,
 * as the address of the archive. Behind the real edge it resolves to the right
 * HOST but the wrong SCHEME, because that nginx block forwards Host and not
 * X-Forwarded-Proto (see config/settings.ts for the full trace).
 *
 * The home page is the single most linked-to URL on the site and the one a
 * crawler treats as the root of the whole tree, so "usually right, unless a
 * proxy header is missing" is the wrong reliability class for it. baseUrl is
 * an env fact (RAZZLE_SITE_ID binds one instance to one archive) and therefore
 * identical server-side and client-side, so this renders once, matches on
 * hydration, and cannot drift from the brand.
 *
 * react-helmet dedupes `link[rel=canonical]` and keeps the LAST one rendered;
 * this component renders inside SiteHome, below core's ContentMetadataTags, so
 * exactly one canonical is emitted and it is this one.
 *
 * The JSON-LD is `WebSite`, not the `DigitalDocument`-ish thing the content
 * pages emit — the root is not a document, it is the archive itself:
 *
 *   - `potentialAction: SearchAction` declares the full-text endpoint in the
 *     form Google reads for a sitelinks search box. It is the structured-data
 *     twin of what llms.txt says in prose (see express/llmsTxt.js), and it is
 *     honest: `/search?SearchableText=` is exactly the URL the site's own
 *     search box produces, and core's withSearch seeds SearchableText from the
 *     URL on first render, so a cold hit on that link really does run the
 *     query. (Facets do NOT seed that way — see helpers/useSearchControls —
 *     which is precisely why the declared target carries only the term.)
 *   - `isPartOf` names the sibling archives. They are three separately-branded
 *     hosts serving one corpus that cross-references itself constantly; saying
 *     so once at the root is cheaper and more accurate than repeating it per
 *     document.
 */
const SiteSEO = ({ content }) => {
  const site = useSiteConfig(content);
  const origin = (site?.baseUrl || '').replace(/\/+$/, '');
  if (!origin) return null;

  /**
   * The site root names the archive and its publisher, and nothing else does.
   *
   * `seoName` carries the corpus ("DOE Directives Archive"); the brand suffix
   * carries the operator. Both are needed here because the SERP line is the one
   * surface where the footer disclaimer cannot reach a reader, and it is where
   * a false impression of a government site would form. `seoDescription` puts
   * the non-affiliation sentence into the snippet directly beneath it. See
   * SiteConfig.seoName for the naming rule and why `title` alone was wrong.
   */
  const title = `${site.seoName} | ${PUBLISHER_NAME}`;
  const description = site.seoDescription;

  const siblings = SITE_CHOICES.filter((s) => s.value !== site.id)
    .map((s) => ({
      name: s.label,
      url: (SITE_BASE_URLS[s.value] || '').replace(/\/+$/, ''),
    }))
    .filter((s) => s.url);

  const jsonld = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: site.seoName,
    alternateName: content?.title || undefined,
    url: `${origin}/`,
    description,
    inLanguage: 'en',
  // BL-0082: name the operator in structured data too, so the
  // non-affiliation is machine-readable and not only prose in the footer.
  // Google attributes the page to this Organization rather than inferring
  // a government publisher from the corpus.
  publisher: {
    '@type': 'Organization',
    name: PUBLISHER_LEGAL_NAME,
    url: 'https://www.doxcelerate.com/',
  },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin}/search?SearchableText={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
  if (siblings.length) {
    jsonld.isPartOf = siblings.map((s) => ({
      '@type': 'WebSite',
      name: s.name,
      url: `${s.url}/`,
    }));
  }

  /**
   * Search-console ownership tags, emitted on the site root only — which is
   * where both consoles look.
   *
   * Built as an array rather than inline `{cond && <meta/>}` because
   * react-helmet is fussy about non-element children, and an empty array is
   * unambiguous.
   *
   * A token is only emitted when it is a non-empty string. `null` emits
   * nothing, which is "not verified yet"; emitting `content=""` would be a
   * FAILED verification, which is worse than an absent one because the console
   * reports it as a mismatch rather than as unconfigured.
   *
   * There is no verification without this: measured 2026-08-14, none of the
   * three archives carried a tag from either console, which is why every SEO
   * change to date has shipped unmeasured (BL-0027). GA4 sees only what happens
   * after a click; the queries, impressions and average position that say
   * whether a title change worked are only in Search Console.
   */
  const verification = site.verification || {};
  const verificationTags = [
    ['google-site-verification', verification.google],
    ['msvalidate.01', verification.bing],
  ]
    .filter(([, token]) => typeof token === 'string' && token.trim())
    .map(([name, token]) => (
      <meta key={name} name={name} content={token.trim()} />
    ));

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index,follow" />
      <link rel="canonical" href={`${origin}/`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={PUBLISHER_NAME} />
      <meta property="og:url" content={`${origin}/`} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {verificationTags}
      <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
    </Helmet>
  );
};

export default SiteSEO;
